import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Stack,
    Button,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Pagination,
    Input,
    IconButton,
    Collapse,
    Skeleton,
} from "@mui/material";
import { Add, Search } from "@mui/icons-material";
import { ReactComponent as BFilterSvg } from "../../assets/BlueFilter.svg";
import { ReactComponent as CrossSvg } from "../../assets/CrossIcon.svg";
import { SortHeader, getHighlightedText } from "../../utils/helpers";
import { useNavigate } from "react-router";
import { useAccountMode } from "../../utils/accountMode";
import { useStore } from "react-redux";
import { fetchCustomers } from "./api";


// ======= tweakables to match design exactly =======
const UI = {
    radius: 10,
    headerBg: "#F3F5F7",            // grey top control bar
    tableHeadBg: "#F6F7F8",          // grey table header
    border: "#E6E8EB",
    statusActive: "#1976d2",         // blue dot
    statusInactive: "#9E9E9E",
    fontFamily: "Rubik, Inter, system-ui, -apple-system, Segoe UI, Roboto",
    rowHeight: 56,
    headHeight: 48,
    cellPx: 16,
    titleFs: 18,
    bodyFs: 14,
};

export default function KundenTable() {
    const { prefixPath } = useAccountMode();
    const uiLanguage = useStore().getState().uiLanguage || "de";

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("alle"); // alle | active | inactive
    const [portalFilter, setPortalFilter] = useState("alle");
    const [customerFilter, setCustomerFilter] = useState("alle");
    const [orderBy, setOrderBy] = useState("name");
    const [order, setOrder] = useState("asc");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filterCollapsed, setFilterCollapsed] = useState(false);
    const [apiRows, setApiRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasApiError, setHasApiError] = useState(false);

    const navigate = useNavigate();
    const labels = {
        de: {
            title: "Kundenliste",
            searchPlaceholder: "Kunden durchsuchen",
            filterButton: "Filter",
            statusLabel: "Kundenstatus",
            customersLabel: "Kunden",
            portalLabel: "Kundenportal",
            locationsLabel: "Standorte",
            machinesLabel: "Maschinen",
            newButton: "NEU",
            all: "alle",
            active: "aktiv",
            inactive: "inaktiv",
            ariaLabel: "Kundenliste",
            empty: "Keine Kunden gefunden",
            apiError: "Kunden konnten nicht aus der API geladen werden.",
        },
        en: {
            title: "Customer list",
            searchPlaceholder: "Search customers",
            filterButton: "Filters",
            statusLabel: "Customer status",
            customersLabel: "Customers",
            portalLabel: "Customer portal",
            locationsLabel: "Sites",
            machinesLabel: "Machines",
            newButton: "NEW",
            all: "all",
            active: "active",
            inactive: "inactive",
            ariaLabel: "Customer list",
            empty: "No customers found",
            apiError: "Customers could not be loaded from the API.",
        },
    }[uiLanguage] || {
        title: "Kundenliste",
        searchPlaceholder: "Kunden durchsuchen",
        filterButton: "Filter",
        statusLabel: "Kundenstatus",
        customersLabel: "Kunden",
        portalLabel: "Kundenportal",
        locationsLabel: "Standorte",
        machinesLabel: "Maschinen",
        newButton: "NEU",
        all: "alle",
        active: "aktiv",
        inactive: "inaktiv",
        ariaLabel: "Kundenliste",
        empty: "Keine Kunden gefunden",
        apiError: "Kunden konnten nicht aus der API geladen werden.",
    };
    const statusLabels = {
        de: {
            veröffentlicht: "veröffentlicht",
            "in Planung": "in Planung",
            offline: "offline",
            "nicht eingerichtet": "nicht eingerichtet",
        },
        en: {
            veröffentlicht: "published",
            "in Planung": "planned",
            offline: "offline",
            "nicht eingerichtet": "not configured",
        },
    };

    useEffect(() => {
        let active = true;

        setIsLoading(true);
        setHasApiError(false);

        fetchCustomers()
            .then((rows) => {
                if (active) {
                    setApiRows(rows);
                }
            })
            .catch(() => {
                if (active) {
                    setApiRows([]);
                    setHasApiError(true);
                }
            })
            .finally(() => {
                if (active) {
                    setIsLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, []);

    // Unique customers list for the Kunden select
    const customerOptions = useMemo(
        () => ["alle", ...Array.from(new Set(apiRows.map((r) => r.name).filter(Boolean)))],
        [apiRows]
    );
    const portalOptions = useMemo(
        () => Array.from(new Set(apiRows.map((r) => r.portal).filter(Boolean))),
        [apiRows]
    );

    // helper: lowercases + strips accents (so "veröffentlicht" matches "veroffentlicht")
    const norm = (v) =>
        String(v)
            .toLowerCase()
            .normalize("NFD")
            .replace(/\p{Diacritic}/gu, "");

    // ----- filtering -----
    const filtered = useMemo(() => {
        const tokens = norm(search).split(/\s+/).filter(Boolean);

        return apiRows.filter((r) => {
            // build one searchable blob
            const searchable = [
                r.name,
                r.portal,
                r.locations,           // numbers searchable too
                r.machines,
                r.active ? "aktiv" : "inaktiv",
            ]
                .map(norm)
                .join(" ");

            // AND-match on all tokens
            const matchesSearch =
                tokens.length === 0 || tokens.every((t) => searchable.includes(t));

            const matchesStatus =
                statusFilter === "alle" ||
                (statusFilter === "active" && r.active) ||
                (statusFilter === "inactive" && !r.active);

            const matchesPortal =
                portalFilter === "alle" || r.portal === portalFilter;

            const matchesCustomer =
                customerFilter === "alle" || r.name === customerFilter;

            return matchesSearch && matchesStatus && matchesPortal && matchesCustomer;
        });
    }, [apiRows, search, statusFilter, portalFilter, customerFilter]);
    
    useEffect(() => {
        setPage(0);
    }, [search, statusFilter, portalFilter, customerFilter, rowsPerPage]);


    // ----- sorting -----
    const sorted = useMemo(() => {
        return [...filtered].sort((a, b) => {
            let comp = 0;
            const va = a[orderBy];
            const vb = b[orderBy];
            if (va < vb) comp = -1;
            if (va > vb) comp = 1;
            return order === "asc" ? comp : -comp;
        });
    }, [filtered, orderBy, order]);


    // ----- pagination -----
    const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
    const paginated = useMemo(() => {
        const start = page * rowsPerPage;
        return sorted.slice(start, start + rowsPerPage);
    }, [sorted, page, rowsPerPage]);

    const handleSort = (col) => {
        if (orderBy === col) setOrder(order === "asc" ? "desc" : "asc");
        else {
            setOrderBy(col);
            setOrder("asc");
        }
    };

    const handleFilterBtnclick = () => {

        setFilterCollapsed(!filterCollapsed);
        setStatusFilter("alle");
        setPortalFilter("alle");
        setCustomerFilter("alle");
    }

    const handleNewKunden = () => {
        navigate(prefixPath("/new-kunden"));
    }

    return (
        <div className="kundentable_wrapper bxShadow">

            <div className="_header">
                <div className="_title">
                    {labels.title} ({filtered.length}) <Button variant="contained" onClick={handleNewKunden}> <Add /> &nbsp; {labels.newButton} </Button>
                </div>
                <div className="search_bar area_disabled">
                    <FormControl sx={{ m: 1, width: "100%" }} variant="standard">
                        <Input
                            fullWidth
                            placeholder={labels.searchPlaceholder}
                            id="standard-adornment-password"
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                    // onClick={handleClickShowPassword}
                                    // onMouseDown={handleMouseDownPassword}
                                    // onMouseUp={handleMouseUpPassword}
                                    >
                                        <Search />
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                </div>
                <div className="filters area_disabled" >
                    <Button startIcon={filterCollapsed ? <CrossSvg /> : <BFilterSvg />} variant={filterCollapsed ? "outlined" : "text"} onClick={handleFilterBtnclick} >
                        {labels.filterButton}
                    </Button>
                </div>
            </div>

            {/* Top toolbar (matches screenshot layout) */}
            <Collapse in={filterCollapsed}>
                <Box className="filtersCollapse" sx={{ bgcolor: "rgba(33, 33, 33, 0.05)", borderBottom: `1px solid ${UI.border}`, marginTop: "28px" }}>
                    <Stack spacing={2} sx={{ py: "16px", px: "24px" }}>

                        <Stack direction="row" gap={2} flexWrap="wrap" >
                            <FormControl size="small" sx={{ minWidth: 150 }} variant="standard">
                                <InputLabel>{labels.statusLabel}</InputLabel>
                                <Select
                                    label={labels.statusLabel}
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="alle">{labels.all}</MenuItem>
                                    <MenuItem value="active">{labels.active}</MenuItem>
                                    <MenuItem value="inactive">{labels.inactive}</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 150, marginLeft: "24px" }} variant="standard">
                                <InputLabel>{labels.customersLabel}</InputLabel>
                                <Select
                                    label={labels.customersLabel}
                                    value={customerFilter}
                                    onChange={(e) => setCustomerFilter(e.target.value)}
                                >
                                    {customerOptions.map((c) => (
                                        <MenuItem key={c} value={c}>
                                            {c}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 150, marginLeft: "24px" }} variant="standard">
                                <InputLabel>{labels.portalLabel}</InputLabel>
                                <Select
                                    label={labels.portalLabel}
                                    value={portalFilter}
                                    onChange={(e) => setPortalFilter(e.target.value)}
                                >
                                    <MenuItem value="alle">{labels.all}</MenuItem>
                                    {portalOptions.map((s) => (
                                        <MenuItem key={s} value={s}>
                                            {statusLabels[uiLanguage]?.[s] || s}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box sx={{ flex: 1 }} />

                            {/* <TextField
                            size="small"
                            placeholder="Kunden durchsuchen"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ width: 320, ml: "auto" }}
                        /> */}
                        </Stack>
                    </Stack>
                </Box>
            </Collapse>


            {/* Table */}
            <Table
                stickyHeader
                aria-label={labels.ariaLabel}

                sx={{
                    padding: "0px 24px",
                    "& th, & td": {
                        // fontSize: UI.bodyFs,
                        borderBottom: `1px solid ${UI.border}`,
                        px: `${UI.cellPx}px`,
                    },
                    "& th": {
                        // height: UI.headHeight,
                        // lineHeight: `${UI.headHeight}px`,
                        bgcolor: "#FFFFFF",
                        // fontWeight: 600,

                        color: "var(--color-primary)",
                        fontFeatureSettings: "'liga' off, 'clig' off",
                        fontFamily: "Roboto",
                        fontSize: 16,
                        fontStyle: "normal",
                        fontWeight: "500",
                        letterSpacing: 0.15,
                        padding: "36px 24px 16px 0px",

                    },
                    "& td": {
                        // height: UI.rowHeight,
                        color: "#38748D",
                        fontFeatureSettings: "'liga' off, 'clig' off",
                        fontFamily: "Roboto",
                        fontSize: 16,
                        fontStyle: "normal",
                        fontWeight: "400",
                        letterSpacing: 0.5,
                        padding: "16px 24px 16px 0px",
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        {/* Status (first column) */}
                        <TableCell
                            sortDirection={orderBy === "active" ? order : false}
                            sx={{ width: 40, paddingLeft: 0 }}
                        >
                            <SortHeader
                                id="active"
                                orderBy={orderBy}
                                order={order}
                                setOrderBy={setOrderBy}
                                setOrder={setOrder}
                            >

                            </SortHeader>
                        </TableCell>

                        <TableCell sx={{ paddingLeft: 0 }}>
                            <SortHeader
                                id="name"
                                orderBy={orderBy}
                                order={order}
                                setOrderBy={setOrderBy}
                                setOrder={setOrder}
                            >
                                {labels.customersLabel}
                            </SortHeader>
                        </TableCell>

                        <TableCell>
                            <SortHeader
                                id="portal"
                                orderBy={orderBy}
                                order={order}
                                setOrderBy={setOrderBy}
                                setOrder={setOrder}
                            >
                                {labels.portalLabel}
                            </SortHeader>
                        </TableCell>

                        <TableCell>
                            <SortHeader
                                id="locations"
                                orderBy={orderBy}
                                order={order}
                                setOrderBy={setOrderBy}
                                setOrder={setOrder}
                            >
                                {labels.locationsLabel}
                            </SortHeader>
                        </TableCell>

                        <TableCell>
                            <SortHeader
                                id="machines"
                                orderBy={orderBy}
                                order={order}
                                setOrderBy={setOrderBy}
                                setOrder={setOrder}
                            >
                                {labels.machinesLabel}
                            </SortHeader>
                        </TableCell>

                    </TableRow>
                </TableHead>

                <TableBody>
                    {isLoading ? Array.from({ length: rowsPerPage }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                            <TableCell sx={{ width: 40, paddingLeft: 0 }}>
                                <Skeleton variant="circular" width={10} height={10} />
                            </TableCell>
                            <TableCell sx={{ paddingLeft: 0 }}>
                                <Skeleton variant="text" width={180} height={28} />
                            </TableCell>
                            <TableCell>
                                <Skeleton variant="text" width={120} height={28} />
                            </TableCell>
                            <TableCell align="right">
                                <Skeleton variant="text" width={24} height={28} />
                            </TableCell>
                            <TableCell align="right">
                                <Skeleton variant="text" width={24} height={28} />
                            </TableCell>
                        </TableRow>
                    )) : paginated.length > 0 ? paginated.map((row) => (
                        <TableRow key={row.id} hover className="cp">
                            {/* NEW: status dot column */}
                            <TableCell sx={{ width: 40, paddingLeft: 0 }}>
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        bgcolor: row.active ? UI.statusActive : UI.statusInactive,
                                        mx: 0.5,
                                    }}
                                />
                            </TableCell>

                            {/* Kunden (name only now) */}
                            <TableCell onClick={() => {
                                const detailUrl = row.nodeid
                                    ? prefixPath(`/kunden-detail?nodeid=${encodeURIComponent(row.nodeid)}`)
                                    : prefixPath("/kunden-detail");
                                navigate(detailUrl);
                            }}>
                                <Typography sx={{ fontWeight: 500 }}>
                                    {getHighlightedText(row.name, search)}
                                </Typography>
                            </TableCell>

                            {/* Portal */}
                            <TableCell>
                                <Typography
                                    sx={{
                                        fontWeight: 500,
                                        color: row.portal === "offline" ? "rgba(0, 0, 0, 0.38)" : "text.primary",
                                        borderBottom: "1px solid rgba(0, 0, 0, 0.87)",
                                        borderBottomStyle: "dotted",
                                        display: "inline-block",
                                        borderBottomColor:
                                            row.portal === "offline" ? "rgba(0, 0, 0, 0.38)" : "text.primary",
                                    }}
                                >
                                    {getHighlightedText(statusLabels[uiLanguage]?.[row.portal] || row.portal || "—", search)}
                                </Typography>
                            </TableCell>

                            {/* Standorte */}
                            <TableCell align="right">
                                <Box sx={{ minWidth: 20, textAlign: "left" }}>
                                    <Typography
                                        sx={{
                                            color: row.portal === "offline" ? "rgba(0, 0, 0, 0.38)" : "text.primary",
                                            borderBottom: "1px solid rgba(0, 0, 0, 0.87)",
                                            borderBottomColor:
                                                row.portal === "offline" ? "rgba(0, 0, 0, 0.38)" : "text.primary",
                                            borderBottomStyle: "dotted",
                                            display: "inline-block",
                                        }}
                                    >
                                        {row.locations ?? "—"}
                                    </Typography>
                                </Box>
                            </TableCell>

                            {/* Maschinen */}
                            <TableCell align="right">
                                <Box sx={{ minWidth: 20, textAlign: "left" }}>
                                    <Typography
                                        sx={{
                                            color: row.portal === "offline" ? "rgba(0, 0, 0, 0.38)" : "text.primary",
                                            borderBottom: "1px solid rgba(0, 0, 0, 0.87)",
                                            borderBottomColor:
                                                row.portal === "offline" ? "rgba(0, 0, 0, 0.38)" : "text.primary",
                                            borderBottomStyle: "dotted",
                                            display: "inline-block",
                                        }}
                                    >
                                        {row.machines ?? "—"}
                                    </Typography>
                                </Box>
                            </TableCell>

                        </TableRow>
                    )) : (
                        <TableRow>
                            <TableCell colSpan={5} sx={{ padding: "24px 0" }}>
                                <Typography sx={{ color: "rgba(0, 0, 0, 0.5)" }}>
                                    {hasApiError ? labels.apiError : labels.empty}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>

            </Table>

            {/* thin divider to separate table & pagination like screenshot */}

            {/* Pagination - German copy & right-aligned rows-per-page */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 3,
                    color: "var(--color-primary)",
                }}
            >
                {/* Left: Pagination */}
                <Pagination
                    count={Math.max(1, Math.ceil(filtered.length / rowsPerPage))}
                    page={page + 1}
                    onChange={(_, value) => setPage(value - 1)}
                    color="text"
                    shape="rounded"
                />

                {/* Right: Rows per page selector */}

                <FormControl variant="standard" sx={{
                    m: 1, minWidth: 120, fontFamily: "Roboto",
                    fontSize: "16px"
                }}>
                    <Select
                        labelId="demo-simple-select-standard-label"
                        id="demo-simple-select-standard"
                        value={rowsPerPage}
                        onChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        label={uiLanguage === "en" ? "Entries per page" : "Einträge pro Seite"}
                        className="perPageSelect"
                        sx={{ color: "rgba(0, 0, 0, 0.50)" }}
                    >
                        {[10, 25, 50].map((size) => (
                            <MenuItem key={size} value={size}>
                                {size} {uiLanguage === "en" ? "entries per page" : "Einträge pro Seite"}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

        </div>
    );
}
